import { box } from '@fabric-ds/component-classes';
import tracking from '@finn-no/analytics';
import pulse from '@finn-no/pulse-sdk';
import { i18n } from '@lingui/core';
import { css, html, LitElement } from 'lit';

const converter = (value) => {
    if (!value) return null;

    try {
        const v = JSON.parse(value);
        if (Array.isArray(v)) {
            return v.map((val) => val.value || val);
            // eslint-disable-next-line no-else-return
        } else if (v.value) {
            return [v.value];
        }
    } catch (err) {
        // noop
    }

    if (typeof value === 'string') {
        return [value];
    }

    return null;
};

class MFPJob extends LitElement {
    static get properties() {
        return {
            market: { attribute: true, type: String },
            baseurl: { attribute: true, type: String },
            css: { attribute: true, converter },
        };
    }

    static get styles() {
        return css`
            :host {
                display: block;
            }
        `;
    }

    connectedCallback() {
        super.connectedCallback();

        const messages = JSON.parse(this.getAttribute('messages') || '{}');
        const locale = document?.documentElement?.lang;
        i18n.load(locale, messages);
        i18n.activate(locale);

        pulse.trackPageView({
            object: {
                type: 'MarketFrontpage',
            },
            vertical: {
                name: 'job',
            },
        });

        tracking.trackPage({});

        if (navigator.serviceWorker) {
            navigator.serviceWorker.register('/sw.js').catch(() => {});
        }
    }

    render() {
        return html`
            ${this.css.map(
                (style) =>
                    html`
                        <link
                            href="${style}"
                            rel="stylesheet"
                            type="text/css"
                        />
                    `,
            )}
            <a
                href="#market-start"
                class="skiplink absolute top left opacity-0 bg-milk pv-5 ph-10 -z-1"
                >${i18n.t({
                    id: 'job.skiplink',
                    message: 'Go to main content',
                    comment: 'skiplink used on the job market frontpage',
                })}</a
            >
            <slot name="topbar"></slot>
            <slot name="banner"></slot>
            <main class="page-container pt-16" style="min-height: 60vw">
                <slot name="broadcast"></slot>
                <header class="flex items-center mb-16">
                    <span class="text-aqua-400 mr-8">
                        <slot name="icon"></slot>
                    </span>
                    <h1
                        class="h2 mb-0"
                        data-automation-id="marketTitle"
                        id="market-start"
                    >
                        ${this.market}
                    </h1>
                </header>
                <slot name="job-personal-links"></slot>
                <div class="md:grid f-grid md:grid-cols-3 -mx-16">
                    <div class="md:col-span-2">
                        <slot name="global-search"></slot>
                        <slot name="job-links"></slot>
                        <slot name="job-profile-entry"></slot>
                    </div>
                    <div>
                        <slot name="last-search"></slot>
                        <div class="mt-32">
                            <slot name="user-accounts"></slot>
                        </div>
                    </div>
                </div>
                <div class="-mx-16">
                    <slot name="employer-profile-recommendations-top"></slot>
                </div>
                <div class="-mx-16">
                    <slot name="recommendations"></slot>
                </div>
                <div class="-mx-16">
                    <slot name="employer-profile-recommendations-bottom"></slot>
                </div>
                <div class="-mx-16">
                    <slot name="cms-articles"></slot>
                </div>
                <section
                    class="${box.box} bg-blue-50 mb-16"
                    aria-labeledby="contentTips"
                >
                    <h2 class="h3 mb-16" id="contentTips">
                        ${i18n.t({
                            id: 'job.content-tips.title',
                            message:
                                'More popular searches and useful articles',
                            comment: 'Title for the content tips section',
                        })}
                    </h2>
                    <div class="md:grid f-grid md:grid-cols-3">
                        <div>
                            <h3 class="h4 mb-0">Stillinger</h3>
                            <ul class="list list--vertical">
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?occupation=0.59"
                                        >${i18n.t({
                                            id: 'job.content-tips.link.nurse',
                                            message: 'Nurse',
                                            comment:
                                                'link, links to search for nurse jobs',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?occupation=0.20"
                                        >${i18n.t({
                                            id: 'job.content-tips.link.engineer',
                                            message: 'Engineer',
                                            comment:
                                                'link, links to search for engineer jobs',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?occupation=0.23"
                                        >${i18n.t({
                                            id: 'job.content-tips.link.it',
                                            message: 'IT development',
                                            comment:
                                                'link, links to search for it development jobs',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?occupation=1.16.40"
                                        >${i18n.t({
                                            id: 'job.content-tips.link.educator-therapist',
                                            message:
                                                'Health worker and social educator',
                                            comment:
                                                'link, links to search for it development jobs',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?occupation=0.64"
                                        >${i18n.t({
                                            id: 'job.content-tips.teacher',
                                            message:
                                                'Teacher, headmaster and pedagogue',
                                            comment:
                                                'link, links to search for teacher and equivalent jobs',
                                        })}</a
                                    >
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h3 class="h4 mb-0">Steder</h3>
                            <ul class="list list--vertical">
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?location=1.20001.20061"
                                        >Oslo</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?location=2.20001.22046.20220"
                                        >Bergen</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?location=2.20001.20016.20318"
                                        >Trondheim</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?location=2.20001.20012.20196"
                                        >Stavanger</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/fulltime/search.html?location=2.20001.22054.20413"
                                        >Tromsø</a
                                    >
                                </li>
                            </ul>
                        </div>
                        <div>
                            <h3 class="h4 mb-0">Artikler</h3>
                            <ul class="list list--vertical">
                                <li>
                                    <a href="${this.baseurl}/job/artikler"
                                        >${i18n.t({
                                            id: 'job.articles.list.tips.link',
                                            message:
                                                'Tips and tricks for the job market',
                                            comment:
                                                'link, links to article on the job market',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/artikler/jobbsoknad"
                                        >${i18n.t({
                                            id: 'job.articles.list.application.link',
                                            message:
                                                'Job application, some good tips',
                                            comment:
                                                'link, links to article on tips for the job application',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/artikler/jobbintervju"
                                        >${i18n.t({
                                            id: 'job.articles.list.interview.link',
                                            message:
                                                'Tips to nail the job interview',
                                            comment:
                                                'link, links to article on how to nail the job interview',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/artikler/cv-tips"
                                        >${i18n.t({
                                            id: 'job.articles.list.cv.link',
                                            message: 'Tips for your CV',
                                            comment:
                                                'link, links to article on how to set up your CV',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a href="${this.baseurl}/job/profile/cv"
                                        >${i18n.t({
                                            id: 'job.articles.list.cv.template.link',
                                            message: 'CV-template',
                                            comment:
                                                'link, links to cv-builder landing page',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a href="${this.baseurl}/job/artikler/lonn"
                                        >${i18n.t({
                                            id: 'job.articles.list.salary',
                                            message: 'Salary',
                                            comment:
                                                'link, links to article on salary',
                                        })}</a
                                    >
                                </li>
                                <li>
                                    <a
                                        href="${this
                                            .baseurl}/job/artikler/karriere"
                                        >${i18n.t({
                                            id: 'job.articles.list.career',
                                            message: 'Career',
                                            comment:
                                                'link, links to article on career',
                                        })}</a
                                    >
                                </li>
                            </ul>
                        </div>
                    </div>
                </section>
            </main>
            <slot name="footer"></slot>
        `;
    }
}

customElements.define('mfp-mc', MFPJob);
